import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../Data/ReduxViewModels/WorksheetEditor';
import { actions as gridActions } from '../../../Data/ReduxViewModels/WorksheetGrid';
import { Button, Dialog, Message } from '..';
import { FaExclamationTriangle } from 'react-icons/fa';
import deepEquals from 'Lib/Object/deepEquals';
import { Table } from 'Components/Layout';
import { pipe } from 'ramda';
import { IWorksheetComponent, WorksheetEditorInjectedPropsProvider } from 'Components/Worksheets/Core';
import { NamePrefixModifier } from 'Components/Core/InjectedPropsContext';
import { hasNonEmptyValue, valueOrDefault } from 'Lib/Any';
import { GridMassEntryChangesGrid, WorksheetChangesGrid } from './SyncDialogContents';
import { getGridDefinition } from 'Components/WorksheetGrids/WorksheetGridDefinitions';
import { GridDefinition } from 'Components/WorksheetGrids';

// Contents component is separate so changing the sync model doesn't cause this to render repeatedly.
const SyncErrorDialogContents: React.FC = () => {
  const clientModel = useSelector((s) => s.worksheetEditor.syncModel?.data, deepEquals);
  const serverModel = useSelector((s) => s.worksheetEditor.syncModel_synced?.data, deepEquals);
  return (
    <section
      style={{
        display: 'grid',
        rowGap: '10px',
      }}
    >
      <Message warning>
        <FaExclamationTriangle size="24" style={{ marginBottom: '-4px' }} />
        The saving of data for the worksheet has failed. Please check your internet connection.
      </Message>
      These changes have not been saved:
      <Table
        colHeaders={['Field', 'Your Value', 'Original Value']}
        colWidths={[250]}
        data={WorksheetChangesGrid({ clientModel, serverModel })}
        alternateRowShading={true}
      />
    </section>
  );
};

const SyncErrorDialog: React.FC = () => {
  const unresolvedConflict = useSelector((s) => s.worksheetEditor.syncStatus === 'syncError');
  const syncParams = useSelector((s) => s.worksheetEditor.syncParams_synced, deepEquals);
  const dispatch = useDispatch();

  return (
    <WorksheetEditorInjectedPropsProvider>
      <NamePrefixModifier namePrefix={'data'}>
        <Dialog
          title="Saving Failed"
          alwaysEnabled
          visible={unresolvedConflict}
          width="500px"
          buttons={[
            <Button primary key="2" autoFocus onClick={() => dispatch(actions.syncErrorResolve({ sync: true }))}>
              Try Again
            </Button>,
            <Button key="1" onClick={() => dispatch(actions.syncResolve({ syncPatch: { syncParams }, sync: false }))}>
              Refresh (unsaved changes will be lost)
            </Button>,
            <Button key="0" onClick={() => dispatch(actions.syncErrorResolve({ sync: false }))}>
              Cancel
            </Button>,
          ]}
        >
          {unresolvedConflict && <SyncErrorDialogContents />}
        </Dialog>
      </NamePrefixModifier>
    </WorksheetEditorInjectedPropsProvider>
  );
};

interface ISyncDialogContents extends IWorksheetComponent {
  data: any[];
  gridDefinition: GridDefinition;
}

const GridSyncErrorDialogContents: React.FC<ISyncDialogContents> = ({ data, gridDefinition }) => {
  const gridSettings = gridDefinition.syncDialogSettings;
  return (
    <section
      style={{
        display: 'grid',
        rowGap: '10px',
      }}
    >
      <Message warning>
        <FaExclamationTriangle size="24" style={{ marginBottom: '-4px' }} />
        The saving of data for the worksheet has failed. Please check your internet connection.
      </Message>
      These changes have not been saved:
      <Table
        colHeaders={[gridSettings.identifierHeader, 'Field', 'Your Value', 'Original Value']}
        colWidths={[150, 250, 250, 250]}
        data={data}
        alternateRowShading={true}
      />
    </section>
  );
};

const GridSyncErrorDialog: React.FC = () => {
  const unresolvedConflict = useSelector((s) => s.worksheetGrid.status.syncError);
  const gridType = useSelector((s) => s.worksheetGrid.gridType);
  const worksheets = useSelector(
    pipe(
      (s: any) => Object.values(s.worksheetGrid.worksheets),
      valueOrDefault([] as Array<any>),
      (worksheets) => worksheets.filter((w) => w.status.syncError === true)
    ),
    deepEquals
  );
  const [changesGrid, setChangesGrid] = useState<string[][] | null>(null);
  const gridDefinition = getGridDefinition(gridType);
  const dispatch = useDispatch();

  const tryAgain = () =>
    worksheets.map((w: any) => {
      return dispatch(gridActions.syncErrorResolve({ worksheetId: w.syncModel.data.Id, retry: true }));
    });

  const refresh = () =>
    worksheets.map((w: any) => {
      return dispatch(
        gridActions.syncErrorResolve({
          worksheetId: w.syncModel.data.Id,
          syncPatch: { serverModelReceived: w.syncModel_synced, syncParams: w.syncParams },
        })
      );
    });

  const cancel = () =>
    worksheets.map((w: any) => {
      return dispatch(gridActions.syncErrorReject({ worksheetId: w.syncModel.data.Id }));
    });

  // This is setup to prevent excessive rendering or calculation of the changes. Also prevents us showing the dialog
  // if there are no differences which could occur if a user changes data, cancels the dialog and then manually reverts the changes
  useEffect(() => {
    var changes = unresolvedConflict
      ? GridMassEntryChangesGrid({
          worksheets,
          gridDefinition,
        })
      : null;
    setChangesGrid(changes && changes.length > 0 ? changes : null);
  }, [unresolvedConflict, worksheets, gridDefinition]);

  return (
    <Dialog
      title="Saving Failed"
      alwaysEnabled
      visible={unresolvedConflict && hasNonEmptyValue(changesGrid)}
      width="700px"
      buttons={[
        <Button primary key="2" autoFocus onClick={() => tryAgain()}>
          Try Again
        </Button>,
        <Button key="1" onClick={() => refresh()}>
          Refresh (unsaved changes will be lost)
        </Button>,
        <Button key="0" onClick={() => cancel()}>
          Cancel
        </Button>,
      ]}
    >
      {unresolvedConflict && changesGrid && (
        <GridSyncErrorDialogContents gridDefinition={gridDefinition} data={changesGrid} />
      )}
    </Dialog>
  );
};

export { GridSyncErrorDialog, SyncErrorDialog };
